<template>
    <BasePanel
        headline="lang_GENERIC_CAPTION_BUDGET"
        :alvqa="alvqa"
    >
        <template v-slot:panelContent>
            <v-data-table
                :headers="headers"
                :items="listItems"
                hide-default-footer
                :header-props="{ sortIcon: '' }"
                :no-data-text="$t('lang_GENERAL_MESSAGE_NO_RECORDS_FOUND')"
                :loading-text="$t('lang_GENERAL_LOADING_DATATABLE')"
                :no-results-text="$t('lang_GENERAL_LOADING_DATATABLE')"
                class="noMinWidth"
                :items-per-page="100"
            >
            <!-- TOP SLOT -->
                <template v-slot:top>
                    <v-row class="pb-3 pl-4">
                        <v-col cols="3">
                            <DropdownFilter 
                                context="ReleasesFilterBudgetGroup"
                                label="lang_LABEL_RELEASES_CAPTION_BUDGET_GROUP"
                                :items="budgetGroupItems"
                                :alvqa="alvqa + '-dropdown-filter-budget-group'"
                                :defaultValueNull="true"
                            />
                        </v-col>
                        <v-col 
                            cols="2"
                            class="pt-8"
                            :alvqa="alvqa + '-sum'"
                        >
                            {{ $t('lang_GENERIC_CAPTION_SUM') }}:
                            {{ $n(getSumOfDisplayedRows, 'number') }}
                        </v-col>
                        <v-spacer></v-spacer>
                        <v-col 
                            cols="2"
                            align-self="end"
                        >
                            <BaseButton 
                                label="lang_GENERIC_CAPTION_EXPORT"
                                @click="exportAllBudget"
                                block
                                icon="fa-file-excel"
                                :alvqa="alvqa + '-list-button-export-all'"
                            />
                        </v-col>
                        <v-col 
                            cols="3"
                            align-self="end"
                        >
                            <BaseButton 
                                @click="addNewRow"
                                :disabled="$route.params.alvId === 'new'"
                                label="lang_GENERAL_BUTTON_ADD_ROW"
                                block
                                :alvqa="alvqa + '-button-add-row'"
                            />
                        </v-col>
                    </v-row>
                </template>

            <!-- Headers -->
                <template v-for="(header, i) in headers" v-slot:[`header.${header.value}`]>
                    <span :alvqa="header.alvqa" :key="i">{{ header.text }}</span>
                </template>
            
            <!-- Items -->
                <template v-slot:[`item.budgetGroup`]="{ item }">
                    <div :alvqa="alvqa + '-list-row-text-budget-group'">
                        {{ getBudgetGroup(item.budgetGroup) }}
                    </div>
                </template>
                <template v-slot:[`item.scheduledMonth`]="{ item }">
                    <div :alvqa="alvqa + '-list-row-text-scheduled-month'">
                        {{ item.scheduledMonth !== null ? $d(new Date(item.scheduledMonth), 'alvMonthYearFormat') : '' }}
                    </div>
                </template>
                <template v-slot:[`item.description`]="{ item }">
                    <div :alvqa="alvqa + '-list-row-text-description'">
                        {{ item.description }}
                    </div>
                </template>
                <template v-slot:[`item.amount`]="{ item }">
                    <div 
                        class="text-right"
                        :alvqa="alvqa + '-list-row-text-amount'"
                    >
                        {{ $n(item.amount, 'number') }}
                    </div>
                </template>
                <template v-slot:[`item.country`]="{ item }">
                    <div :alvqa="alvqa + '-list-row-text-country'">
                        {{ $alvPlugin.getTranslatedCountryName(item.country) }}
                    </div>
                </template>
                <template v-slot:[`item.quantity`]="{ item }">
                    <div 
                        class="text-right"
                        :alvqa="alvqa + '-list-row-text-quantity'"
                    >
                        {{ $alvPlugin.getNumberLocalization(item.quantity, 'numberInteger') }}
                    </div>
                </template>

            <!-- Buttons -->
                <template v-slot:[`item.inlineEdit`]="{ item }">
                    <BaseIcon   
                        :alvqa="alvqa + '-list-row-icon-inline-edit'"
                        icon="edit" 
                        tooltipText="lang_GENERAL_CAPTION_ICON_EDIT"   
                        @click.stop="editInlineFunction(item)"
                    />
                </template>
                <template v-slot:[`item.delete`]="{ item }">
                    <BaseIcon 
                        @click="deleteRow(item)"
                        icon="delete"
                        color="iconDelete"
                        tooltipText="lang_GENERIC_CAPTION_DELETE"
                        :alvqa="alvqa + 'list-row-icon-delete'"
                    />
                </template>
            </v-data-table>
        </template>
    </BasePanel>
</template>
    
<script>
import { DropdownLabelReleasesBudgetGroupTypes } from '@/assets/dropdowns/alvDropdowns.js'

import BasePanel from '@/components/_base/BasePanel'
import BaseButton from '@/components/_buttons/BaseButton'
import BaseIcon from '@/components/_buttons/BaseIcon'
import DropdownFilter from '@/components/_filters/DropdownFilter'

export default {
    name: "ReleasesBudgetEditPanel",
    components: {
        BasePanel,
        BaseButton,
        BaseIcon,
        DropdownFilter
    },
    data() {
        return {
            alvqa: 'label-releases-edit-form-panel-budget'
        }
    },
    computed: {
        headers() {
            return [
                {
                    text: this.$t("lang_LABEL_RELEASES_CAPTION_BUDGET_GROUP"), 
                    align: "left", 
                    value: "budgetGroup",
                    class: 'text-no-wrap',
                    alvqa: this.alvqa + '-header-budget-group',
                    sortable: false,
                    filter: this.filterbudgetGroup
                },   
                {
                    text: this.$t("lang_LABEL_RELEASES_CAPTION_SCHEDULE_MONTH"), 
                    align: "left", 
                    value: "scheduledMonth",
                    class: 'text-no-wrap',
                    sortable: false,
                    alvqa: this.alvqa + '-header-scheduled-month'
                }, 
                {
                    text: this.$t("lang_LABEL_RELEASES_CAPTION_DESCRIPTION"), 
                    align: "left", 
                    value: "description",
                    class: 'text-no-wrap',
                    sortable: false,
                    alvqa: this.alvqa + '-header-description'
                },       
                {
                    text: this.$t("lang_GENERIC_CAPTION_AMOUNT") + ' ' + this.$store.getters.getSelectedCurrency, 
                    align: "right", 
                    value: "amount",
                    class: 'text-no-wrap',
                    sortable: false,
                    alvqa: this.alvqa + '-header-amount'
                }, 
                {
                    text: this.$t('lang_GENERAL_CAPTION_COUNTRY'), 
                    value: 'country',
                    align: 'left', 
                    width: '200px',
                    class: 'text-no-wrap',
                    sortable: false,
                    alvqa: this.alvqa + '-header-country'
                },
                {
                    text: this.$t('lang_LABEL_RELEASES_CAPTION_QUANTITY'), 
                    value: 'quantity',
                    align: 'right',  
                    class: 'text-no-wrap',
                    sortable: false,
                    alvqa: this.alvqa + '-header-quantity'
                },
                {
                    text: '', 
                    value: 'inlineEdit',
                    align: 'right',
                    sortable: false,
                    width: "70px",
                    class: 'text-no-wrap'
                },
                {
                    text: "",
                    align: "right",
                    width: "70px",
                    value: "delete",
                    sortable: false,
                    class: 'text-no-wrap'
                }
            ]
        },

        listItems() { return this.$store.getters.getReleasesBudget },
        budgetGroupItems() {
            let types = JSON.parse(JSON.stringify(DropdownLabelReleasesBudgetGroupTypes))

            types.forEach(element => {
                if(element.group === 'income') element.text = this.$t('lang_GENERIC_CAPTION_INCOME') + ': ' + this.$t(element.text)
                if(element.group === 'costs') element.text = this.$t('lang_LABEL_RELEASES_PANEL_CAPTION_COSTS') + ': ' + this.$t(element.text)
            })

            types.sort((a,b) => (a.text > b.text) ? 1 : (b.text > a.text) ? -1 : 0)
            types.unshift(
                {
                    text: this.$t('lang_LABEL_RELEASES_PANEL_CAPTION_COSTS') + ': ' + this.$t('lang_GENERAL_CAPTION_ALL'), 
                    value: 'costsAll',
                    group: 'costs',
                    alvqa: 'dropdown-budget-group-costs-all'
                },
                {
                    text: this.$t('lang_GENERIC_CAPTION_INCOME') + ': ' + this.$t('lang_GENERAL_CAPTION_ALL'), 
                    value: 'incomeAll',
                    group: 'income',
                    alvqa: 'dropdown-budget-group-income-all'
                },
                { divider: true }
            )

            return types
        },
        getSumOfDisplayedRows() {
            let budget = this.$store.getters.getReleasesBudget
            let selected = this.$store.getters.getSelectedReleasesFilterBudgetGroup
            let sum = 0

            // if no filter is set, just sum up the amount of all rows.
            if(!selected) sum = budget.reduce((prev, cur) => prev + cur.amount, 0)
            else { // if the filter is set
                if(selected === 'costsAll') { // if the Costs: All filter is set
                    let costsList = []
                    // create an array of all cost types
                    DropdownLabelReleasesBudgetGroupTypes.forEach(element => {
                        if(element.group === 'costs') costsList.push(element.value)
                    })
                    // find all rows, that are included in this list and add the amount to the sum
                    budget.forEach(row => {
                        if(costsList.includes(row.budgetGroup))  sum += row.amount
                    })
                    
                } else if (selected === 'incomeAll') { // if the Income: All filter is set
                    let incomeList = []
                    // create an array of all income types
                    DropdownLabelReleasesBudgetGroupTypes.forEach(element => {
                        if(element.group === 'income') incomeList.push(element.value)
                    })
                    // find all rows, that are included in this list and add the amount to the sum
                    budget.forEach(row => {
                        if(incomeList.includes(row.budgetGroup))  sum += row.amount
                    })
                } else {
                    // of not an "all" filter is selected just simply add the amount of all the rows to the sum
                    budget.forEach(row => {
                        if(row.budgetGroup === selected) sum += row.amount
                    })
                }
            }
            
            return sum
        }
    },
    methods: {
        getBudgetGroup(budgetGroupValue) {
            let budgetGroupTypes = DropdownLabelReleasesBudgetGroupTypes

            if(budgetGroupTypes !== undefined) {
                let match = budgetGroupTypes.find(type => {
                    return type.value === budgetGroupValue
                })

                if(match !== undefined) {
                    if(match.group === 'income') return this.$t('lang_GENERIC_CAPTION_INCOME') + ': ' + this.$t(match.text)
                    if(match.group === 'costs') return this.$t('lang_LABEL_RELEASES_PANEL_CAPTION_COSTS') + ': ' + this.$t(match.text)
                } else return ''
            } else return ''
        },

        filterbudgetGroup(value) {
            let budgetGroupTypes = DropdownLabelReleasesBudgetGroupTypes
            let selected = this.$store.getters.getSelectedReleasesFilterBudgetGroup

            if (!selected) return true
            else { 
                if(selected === 'costsAll') {
                    let costsList = []
                    budgetGroupTypes.forEach(element => {
                        if(element.group === 'costs') costsList.push(element.value)
                    })
                    return costsList.includes(value)
                } else if (selected === 'incomeAll') {
                    let incomeList = []
                    budgetGroupTypes.forEach(element => {
                        if(element.group === 'income') incomeList.push(element.value)
                    })
                    return incomeList.includes(value)
                } else {
                    return value === selected
                }
            }
        },
        exportAllBudget() {
            this.$store.dispatch('exportAllReleasesBudget')
        },
        deleteRow(item) {
            // set the selected row that should be deleted from the delete action 
            // and open the deletion dialog
            this.$store.commit("setSelectedReleasesBudget", item)
            this.$store.commit('setDeletionDialogContext', 'SelectedReleasesBudget')
            this.$store.commit('setShowDeletionDialog', true)
        },
        addNewRow() {
            this.$store.commit('setShowReleasesBudgetEditingDialog', true)
            this.$store.commit('setEditingDialogHeadline', this.$t('lang_GENERAL_CAPTION_NEW_ENTRY'))

            let defaultValues = JSON.parse(JSON.stringify(this.$store.getters.getDefaultReleasesBudget)) 
            this.$store.commit('setReleasesBudgetEditingDialogDetails', defaultValues)
        },
        editInlineFunction(item) {
            this.$store.commit('setSelectedReleasesBudget', item.id)
            let detailsDeepCopy = JSON.parse(JSON.stringify(item))


            detailsDeepCopy['amount'] = this.$n(detailsDeepCopy['amount'], 'number')

            // set the copy in dialog details to edit
            this.$store.commit('setReleasesBudgetEditingDialogDetails', detailsDeepCopy)
            this.$store.commit('setEditingDialogHeadline', this.$t("lang_GENERAL_CAPTION_EDITING_ENTRY")) // set headline
            this.$store.commit('setShowReleasesBudgetEditingDialog', true) 
        },
    },
    mounted() {
        this.$store.commit('setCountries')
    }
}

</script>